<template>
  <div class="have-teams-workspace-wrapper">
    <MainActions v-if="!isCompletedTournament" v-model="selectedTeams"/>
    <div class="actions">
      <div class="stage-teams-actions">
        <b-input v-model="searchTeamModel"
                 class="search-teams-input-custom"
                 v-b-tooltip.hover.top="'Поиск доступен по названию команды, nick и MLBB_ID'"
                 placeholder="Поиск команды"/>
        <b-button variant="primary"
                  class="stage-teams-button"
                  @click="$emit('changeWorkspace', 'AddTeam')">
          Команды этапа
        </b-button>
      </div>
      <div class="actions-right">
        <b-button v-if="canStartStageAutomation"
                  variant="primary"
                  class="start-stage-automation"
                  :disabled="isStartingStageAutomation"
                  v-b-tooltip.hover.top="'Запустит матчи первых двух раундов, где уже есть 2 команды. Дальше матчи стартуют автоматически по мере формирования пар'"
                  @click="startStageAutomation">
          <b-spinner small v-if="isStartingStageAutomation"/>
          {{ isStartingStageAutomation ? 'Запуск...' : 'Запустить автоматизацию' }}
        </b-button>
        <div class="teams-count">Количество команд в этапе: {{ stageTeamsCount }}</div>
      </div>
    </div>
    <div v-if="test_tree && test_tree.children" class="bracket-scroll">
      <TreeView
          :dataset="displayTree"
          linkStyle="straight"
          direction="horizontal-rtl"
          :config="treeConfig"
          :dragEnabled="false"
      >
        <template v-slot:node="{ node, collapsed }">
          <div class="tournament-match-node">
            <div v-if="isFinalNode(node)" class="match-info final-match">
              <MatchAlertBeacon v-if="hasMatchAlert(node.group_id)"
                                :color="matchAlertColor(node.group_id)"
                                :tooltip="matchAlertTooltip(node.group_id)"/>
              <template v-if="node.teams && node.teams.length > 0">
                <div v-for="(team, index) in node.teams"
                     :key="`${node.name}-${team.name}-${index}`"
                     @click="openNodeModal(node)"
                     :class="['team', { 'winner champion': isTeamWinner(team, node.teams), 'loser': !isTeamWinner(team, node.teams) }]">
                  <span class="team-name">{{ team.name }}</span>
                  <span class="team-score">{{ team.score }}</span>
                </div>
                <div v-for="emptySlot in Math.max(0, 2 - node.teams.length)"
                     :key="`empty-final-${node.name}-${emptySlot}`"
                     @click="openNodeModal(node)"
                     class="team loser empty-team">
                  <span class="team-name"></span>
                  <span class="team-score"></span>
                </div>
              </template>
              <template v-else>
                <div v-for="team in 2"
                     :key="`team-final-empty-${team}`"
                     @click="openNodeModal(node)"
                     class="team loser empty-team">
                  <span class="team-name"></span>
                  <span class="team-score"></span>
                </div>
              </template>
            </div>
            <div v-else-if="node.teams && node.teams.length > 0" class="match-info">
              <MatchAlertBeacon v-if="hasMatchAlert(node.group_id)"
                                :color="matchAlertColor(node.group_id)"
                                :tooltip="matchAlertTooltip(node.group_id)"/>
              <div v-for="(team, index) in node.teams"
                   :key="`${node.name}-${team.name}-${index}`"
                   @click="openNodeModal(node)"
                   :class="['team', { 'winner': isTeamWinner(team, node.teams), 'loser': !isTeamWinner(team, node.teams) }]">
                <span class="team-name">{{ team.name }}</span>
                <span class="team-score">{{ team.score }}</span>
              </div>
              <div v-for="emptySlot in Math.max(0, 2 - node.teams.length)"
                   :key="`empty-${node.name}-${emptySlot}`"
                   @click="openNodeModal(node)"
                   class="team loser empty-team">
                <span class="team-name"></span>
                <span class="team-score"></span>
              </div>
            </div>
            <div v-else class="match-info" @click="openNodeModal(node)">
              <MatchAlertBeacon v-if="hasMatchAlert(node.group_id)"
                                :color="matchAlertColor(node.group_id)"
                                :tooltip="matchAlertTooltip(node.group_id)"/>
              <div v-for="team in 2" :key="`team-none-id-${team}`" class="team loser empty-team">
                <span class="team-name"></span>
                <span class="team-score"></span>
              </div>
            </div>
          </div>
        </template>
      </TreeView>
    </div>
    <div v-else>
      Загрузка данных турнира...
    </div>

    <b-modal v-model="isShowGridMatchModal"
             title="Результаты матча"
             size="xl"
             @hide="closeNodeModal"
             hide-footer
    >
      <OpenGridMatch v-if="selectedNode"
                     :params="{
                       nodeData: selectedNode,
                       stageId: selectedStageId,
                       upperGridTeamCount: null,
                       lowerGridTeamCount: null,
                       onScoreUpdated:handleScoreUpdateChange,
                       onRollback:handleRollbackGroupTeam
                     }"
                     @handleCloseModal="closeNodeModal"
                     @getGrids="getGrids"
      />
    </b-modal>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex'
import MainActions from "@/components/Tournament/MainActions.vue";
import api from "@/api/api";
import OpenGridMatch from "@/components/modals/OpenGridMatch.vue";
import {filterTree} from "@/common/filters";
import {isTeamWinner} from "@/common/isTeamWinner";
import {connectToWs, onMessage, subscribe, unsubscribe} from "@/services/websocket";
import {AuthAdapterMixin} from "@/mixins/Auth/AuthAdapterMixin";
import {MatchAlertsMixin} from "@/mixins/Tournament/MatchAlertsMixin";
import MatchAlertBeacon from "@/components/Tournament/MLBB/components/MatchAlertBeacon.vue";
import {
  parseStageAutomationStart,
  stageAutomationErrorMessage,
  stageAutomationStartMessage
} from "@/services/mlbbAutomation";

export default {
  name: "HaveTeamsWorkspace",
  mixins: [AuthAdapterMixin, MatchAlertsMixin],
  components: {
    MainActions,
    OpenGridMatch,
    MatchAlertBeacon
  },
  computed: {
    ...mapState('moduleNewTournament', ['selectedStageId', 'selectedRoundId', 'groupsWithTeamsByMLBB', 'tournament', 'stageTeamsCount']),
    ...mapGetters('moduleNewTournament', ['selectedGroup', 'selectedStage', 'isCompletedTournament', 'formatBySelectedStage']),
    ...mapGetters('moduleModal', ['getShow', 'getOption', 'getIdModalForDelete']),
    ...mapActions('moduleModal', ['openModal', 'closeModal', 'optionName', 'cleanOption']),
    // Бэкенд запускает автоматизацию только по сетке Single Elimination,
    // для остальных форматов эндпоинт отдаёт 422 — кнопку не показываем
    canStartStageAutomation() {
      return !!this.selectedStageId
          && this.formatBySelectedStage === 'single'
          && !this.isCompletedTournament
    },
    displayTree() {
      if (!this.searchTeamModel) {
        return this.test_tree
      }
      return {
        children: filterTree(this.test_tree.children, this.searchTeamModel)
      }
    }
  },
  data() {
    return {
      ws: null,
      tournamentChannel: null,
      wsUnsubscribe: null,
      isScoreUpdated: false,
      isShowGridMatchModal: false,
      isStartingStageAutomation: false,
      selectedNode: null,
      cloneGroups: [],
      apiGridData: null,
      selectedTeams: [],
      teamsForTransfer: [],
      isLoadingSaveTransferredTeams: false,
      searchTeamModel: '',
      selectedTeamForGrid: null,
      test_tree: {},
      _getGridsTimer: null,
      treeConfig: {
        nodeWidth: 90,
        linkSeparation: 100,
        nodeHeight: 80,
        levelHeight: 130,
        buildMode: 'children',
      }
    }
  },
  methods: {
    isTeamWinner,
    ...mapMutations('moduleNewTournament', ['setFirstLevelData']),
    subscribeToTournamentChannel() {
      const tournamentId = this.tournament?.id
      if (!tournamentId || this.tournamentChannel) return
      this.tournamentChannel = `tournaments.${tournamentId}`
      subscribe(this.tournamentChannel, localStorage.getItem('token'))
    },
    openNodeModal(node) {
      this.selectedNode = node;
      this.isShowGridMatchModal = true;
    },
    async startStageAutomation() {
      const stageId = this.selectedStageId
      if (!stageId || this.isStartingStageAutomation) return

      const confirmed = await this.$bvModal.msgBoxConfirm(
          'Запустить автоматизацию для всех матчей первых двух раундов, где уже собрана пара из 2 команд? ' +
          'Матчи следующих раундов дальше будут запускаться автоматически, как только в них появится вторая команда.',
          {
            title: 'Запуск автоматизации этапа',
            okTitle: 'Запустить',
            cancelTitle: 'Отмена',
            okVariant: 'primary',
            centered: true
          }
      )
      if (!confirmed) return

      try {
        this.isStartingStageAutomation = true
        // Свои тосты информативнее дефолтных из интерцептора: показываем сводку из meta
        const res = await api.grid.startMlbbAutomationForStage(stageId, {
          skipSuccessToast: true,
          skipErrorToast: true
        })
        const result = parseStageAutomationStart(res)

        this.$bvToast.toast(stageAutomationStartMessage(result), {
          title: result.startedCount ? 'Автоматизация запущена' : 'Новых матчей для запуска нет',
          variant: result.startedCount ? 'success' : 'info',
          solid: true,
          autoHideDelay: 4000
        })

        await this.getGrids()
      } catch (e) {
        console.error('Ошибка при запуске автоматизации этапа:', e)
        this.$bvToast.toast(stageAutomationErrorMessage(e), {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
          autoHideDelay: 4000
        })
      } finally {
        this.isStartingStageAutomation = false
      }
    },
    handleWebSocketMessage(message) {
      console.log("MESSAGE", message)
      // Алерты матчей обрабатывает MatchAlertsMixin — сетку перезапрашивать не нужно,
      // вся информация для индикатора приходит в самом событии
      if (this.handleMatchAlertMessage(message)) return

      switch (message.event) {
        case 'groupResult.fields.changed':
          this.handleGroupResultChanged(message.data)
          break;
        case 'tournament.group.team.new':
          this.handleNewTeam(message.data)
          break
        default:
          console.log('Неизвестное событие:', message.event)

      }
    },
    async handleGroupResultChanged(data) {
      console.log("data", data)
      await this.getGrids()

      this.$bvToast.toast('Результаты матча обновлены', {
        title: 'Обновление',
        variant: 'info',
        solid: true,
        autoHideDelay: 2000
      })
    },
    async handleNewTeam() {
      await this.getGrids()
      this.$bvToast.toast('Добавлена новая команда в турнир', {
        title: 'Обновление',
        variant: 'success',
        solid: true,
        autoHideDelay: 2000
      })
    },
    async handleRollbackGroupTeam(groupTeamsId) {
      try {
        const res = await api.tournament.rollback(groupTeamsId)

        if (res.status === 204) {
          this.$bvToast.toast('Откат успешно выполнен', {
            title: 'Успех',
            variant: 'success',
            solid: true,
            autoHideDelay: 2000
          })

          // Закрываем модалку
          this.closeNodeModal()

          // Обновляем данные турнира
          await this.getGrids()
        }
      } catch (e) {
        console.error('Ошибка при откате:', e)
        this.$bvToast.toast(e.response?.data?.message || 'Ошибка при откате', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        })
      }
    },
    closeNodeModal() {
      this.isShowGridMatchModal = false;
      this.selectedNode = null;
    },

    isFinalNode(node) {
      return node && (
          node.next_group_id === null ||
          node.next_group_id === undefined ||
          node.next_round_id === null
      );
    },
    async handleScoreUpdateChange(value) {
      if (value === true) {
        await this.getGrids();
      }
    },

    findNodeByKey(nodes, key) {
      for (const node of nodes) {
        if (node._key === key) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = this.findNodeByKey(node.children, key);
          if (found) return found;
        }
      }
      return null;
    },

    getGrids() {
      clearTimeout(this._getGridsTimer)
      return new Promise((resolve) => {
        this._getGridsTimer = setTimeout(async () => {
          try {
            const res = await api.grid.getGrid(this.selectedStageId);
            const newGridData = res.data.data.grids[0];
            if (!newGridData) {
              console.error('No grid data received from API');
              this.showError('Данные турнира не найдены');
              resolve();
              return;
            }

            if (!newGridData.matches || newGridData.matches.length === 0) {
              console.error('No matches in grid data');
              this.showError('Матчи не найдены');
              resolve();
              return;
            }

            if (!newGridData.teams || newGridData.teams.length === 0) {
              console.warn('No teams in grid data');
            }

            this.apiGridData = newGridData;

            this.test_func();
          } catch (error) {
            console.error('Error loading grid data:', error);
            this.showError('Ошибка при загрузке данных турнира');
          }
          resolve();
        }, 300)
      })
    },

    showError(message) {
      this.$bvToast.toast(message, {
        title: 'Ошибка',
        variant: 'danger',
        solid: true,
        autoHideDelay: 3000
      });
    },

    test_func() {
      if (!this.apiGridData) {
        console.warn('No API data available for tree building');
        return;
      }

      const matches = this.apiGridData.matches || [];
      const teams = this.apiGridData.teams || [];

      if (matches.length === 0) {
        console.warn('No matches to process');
        this.$set(this, 'test_tree', {children: []});
        return;
      }

      const treeData = {
        children: this.buildBracketRecursive(matches, teams)
      };

      this.$set(this, 'test_tree', treeData);
      this.$forceUpdate();
    },

    buildBracketRecursive(matches, teams = []) {
      const byId = new Map(matches.map(m => [m.id, m]));
      const childrenByParent = new Map();
      const teamsMap = new Map(teams.map(t => [t.id, t.name]));
      const contactsById = new Map(teams.map(t => [t.id, t.contacts || []]));


      const groupToRoundMap = new Map();
      if (this.apiGridData && this.apiGridData.groups) {
        this.apiGridData.groups.forEach(group => {
          groupToRoundMap.set(group.id, group.round_id);
        });
      }

      const roundNamesMap = new Map();
      if (this.apiGridData && this.apiGridData.rounds) {
        this.apiGridData.rounds.forEach(round => {
          roundNamesMap.set(round.id, round.name);
        });
      }

      for (const match of matches) {
        const p = match.next_group_id;
        if (p == null) continue;
        if (!childrenByParent.has(p)) childrenByParent.set(p, []);
        childrenByParent.get(p).push(match.id);
      }

      const roots = matches.filter(m => m.next_group_id == null).map(m => m.id);

      function buildNode(id, visiting = new Set()) {
        if (visiting.has(id)) {
          return {name: String(id), children: [], next_round_id: null};
        }
        visiting.add(id);

        const childIds = childrenByParent.get(id) || [];
        const children = childIds.map(cid => buildNode(cid, new Set(visiting)));
        const match = byId.get(id);
        const nextRoundId = match?.next_group_id ? groupToRoundMap.get(match.next_group_id) : null;
        const nextRoundName = nextRoundId ? roundNamesMap.get(nextRoundId) : null;

        if (match && match.teams && match.teams.length > 0) {


          const limitedTeams = match.teams
              .slice(0, 2)
              .map((team, idx) => ({...team, originalIndex: idx}))
          // .sort((a, b) => {
          //   const scoreA = a.result_fields?.winner_matches_count || 0;
          //   const scoreB = b.result_fields?.winner_matches_count || 0;
          //   return scoreB - scoreA;
          // });

          const processedTeams = limitedTeams.map((team, index) => {
            const teamName = teamsMap.get(team.id) || `Team ${team.id}`;
            const score = team.result_fields?.winner_matches_count || 0;

            let isWinner = team.result_fields?.is_winner === 1
            if (!isWinner && index === 0 && limitedTeams.length > 1) {
              const nextScore = limitedTeams[1]?.result_fields?.winner_matches_count || 0;
              if (score > nextScore) {
                isWinner = true;
              }
            }


            return {
              name: teamName,
              score: score,
              isWinner: team.result_fields.is_winner === 1,
              id: team.id,
              group_result_id: team.group_result_id,
              contacts: contactsById.get(team.id) || []
            };
          });


          return {
            name: `Match ${id}`,
            _key: `match-${id}`,
            children: children.length ? children : [],
            next_round_id: nextRoundId,
            next_round_name: nextRoundName,
            current_round_id: match.round_id,
            group_id: match.group_id,
            ...(match || {}),
            teams: processedTeams,
            originalTeams: match.teams
          };
        }


        return {
          name: String(id),
          _key: `match-${id}`,
          children: children.length ? children : [],
          next_round_id: nextRoundId,
          next_round_name: nextRoundName,
          current_round_id: match?.round_id,
          group_id: match?.group_id,
          ...(match || {}),
        };
      }

      const result = roots.map(rid => buildNode(rid));
      return result;
    },
  },
  watch: {
    groupsWithTeamsByMLBB: {
      deep: true,
      immediate: true,
      handler() {
        this.cloneGroups = JSON.parse(JSON.stringify(this.groupsWithTeamsByMLBB || []))
      }
    },
    'tournament.id': {
      immediate: true,
      handler() {
        this.subscribeToTournamentChannel()
      }
    },
  },
  mounted() {
    // Сначала подключаемся к WebSocket
    this.ws = connectToWs()

    // Затем устанавливаем обработчик сообщений
    this.wsUnsubscribe = onMessage((data) => this.handleWebSocketMessage(data))

    this.subscribeToTournamentChannel()

    this.getGrids()
  },
  beforeDestroy() {
    clearTimeout(this._getGridsTimer)
    if (this.tournamentChannel) {
      unsubscribe(this.tournamentChannel)
      this.tournamentChannel = null
    }
    if (typeof this.wsUnsubscribe === 'function') {
      this.wsUnsubscribe()
      this.wsUnsubscribe = null
    }
  }
}
</script>

<style scoped lang="scss">
.list-complete-item {
  transition: all 1s;
  display: inline-block;
  margin-right: 10px;
}

.list-complete-enter, .list-complete-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.list-complete-leave-active {
  position: absolute;
}

.have-teams-workspace-wrapper {
  .card-wrapper {
    background: white;
    padding: 20px;
    border-radius: 10px;
    margin-bottom: 20px;
  }

  .group-cards {
    & > *:not(:last-child) {
      margin-bottom: 20px;
    }
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 20px;

    .stage-teams-actions {
      display: flex;
      align-items: center;
      gap: 10px;

      @media screen and (max-width: 500px) {
        flex-wrap: wrap;
      }
    }

    .stage-teams-button {
      border-radius: 15px;
      font-size: 11px;
      padding: 5px 18px;
      white-space: nowrap;
    }

    @media screen and (max-width: 500px) {
      display: block;
    }

    .actions-right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;

      @media screen and (max-width: 500px) {
        margin-top: 10px;
        justify-content: flex-start;
        flex-wrap: wrap;
      }
    }

    .start-stage-automation {
      font-size: 13px;
      white-space: nowrap;
    }

    .teams-count {
      background-color: white;
      border-radius: 0.25rem;
      padding: 0.375rem 0.75rem;
      border: 1px solid #ced4da;
      color: #707070;
      font-size: 13px;

      @media screen and (max-width: 500px) {
        margin-top: 10px;
        width: fit-content;
      }
    }

    .buttons {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;

      @media screen and (max-width: 500px) {
        margin-top: 14px;
        justify-content: flex-start;
      }
    }

    .search-teams-input-custom {
      font-size: 13px;
    }
  }
}

.tournament-match-node {
  min-width: 120px;
  max-width: 120px;
  font-size: 10px;
  padding: 6px;
}

::v-deep .tree-view .node-container {
  &:has(.final-match) {
    &::after {
      display: none !important; // скрывает линии после финала
    }
  }
}

.final-match {
  position: relative;
  // стили что убрать лишний блок
  &::after {
    content: '';
    position: absolute;
    right: -200px;
    top: 0;
    width: 200px;
    height: 100%;
    background: #f8f9fa;
    z-index: 10;
  }
}


.match-info {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: 40px;
}

.team {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  min-height: 28px;
  transition: all 0.3s ease;

  &.loser {
    background: #e5e5e5 !important;
    color: #7d7d8d !important;
  }

  &.winner {
    background: #0427c4 !important;
    font-weight: bold;
    color: white !important;
  }

  &.winner.champion {
    background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%) !important;
    color: #000 !important;
    font-weight: bold;
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
    border: 2px solid #FFD700 !important;
  }
}

.team-name {
  font-weight: 500;
  font-size: 11px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-score {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 10px;
}

.match-title {
  text-align: center;
  font-weight: bold;
}

.bracket-scroll {
  background: #f8f9fa;
  padding: 20px;
  /* фиксируем окно сетки по вьюпорту, чтобы скролл был один — внутри сетки,
     а не страница + блок одновременно */
  height: calc(100vh - 200px);
  min-height: 420px;
}

/**
 * Узлы сетки библиотека позиционирует абсолютно: в высоту блока они не входят,
 * а сам контейнер идёт с overflow:hidden. Поэтому нижние матчи (десктоп) и
 * правые (мобилка) просто обрезались — доскроллить до них было нельзя
 * ни внутри блока, ни страницей, её .page-wrapper тоже режет переполнение.
 */
::v-deep .tree-container {
  overflow: auto !important;
  height: 100% !important;

  /* Узел у библиотеки 90px с translate(-50%,-50%), а карточка матча внутри
     132px и центрируется — крайняя колонка уходит в минус примерно на 26px.
     Отрицательную сторону overflow:auto не скроллит, и край обрезался.
     Сдвигаем оба слоя (линии и узлы) внутрь окна. */
  > svg,
  .dom-container {
    left: 40px;
    top: 40px;
  }

  /* раньше скроллбар был обнулён до 0×0 и не показывался даже там, где был */
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 1px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
}

::v-deep .tree-view {
  min-height: 600px;
}

</style>